import { useRef, useCallback } from 'react';

/**
 * Custom hook to efficiently find the current item index based on playback time.
 * Uses caching to optimize sequential playback (O(1) for adjacent items).
 * Falls back to binary search for larger time jumps (O(log n)).
 */
export const useCurrentItemIndex = (
  items: { startTime: number; endTime: number }[],
  rangeStartTime?: number,
  rangeEndTime?: number
) => {
  const lastItemIndexRef = useRef<number>(-1);

  const getCurrentItemIndex = useCallback(
    (currentTime: number): number => {
      if (
        items.length === 0 ||
        !currentTime ||
        (rangeStartTime !== undefined && currentTime < rangeStartTime) ||
        (rangeEndTime !== undefined && currentTime > rangeEndTime)
      ) {
        return -1;
      }

      const lastIndex = lastItemIndexRef.current;

      // Check if we can use the cached index as a starting point
      if (lastIndex >= 0 && lastIndex < items.length) {
        const lastItem = items[lastIndex];

        // If still in the same item, return it
        if (
          currentTime >= lastItem.startTime &&
          currentTime <= lastItem.endTime
        ) {
          return lastIndex;
        }

        // Check the next item (common case during playback)
        if (lastIndex + 1 < items.length) {
          const nextItem = items[lastIndex + 1];
          if (
            currentTime >= nextItem.startTime &&
            currentTime <= nextItem.endTime
          ) {
            lastItemIndexRef.current = lastIndex + 1;
            return lastIndex + 1;
          }
        }

        // Check the previous item (in case of slight rewind)
        if (lastIndex > 0) {
          const prevItem = items[lastIndex - 1];
          if (
            currentTime >= prevItem.startTime &&
            currentTime <= prevItem.endTime
          ) {
            lastItemIndexRef.current = lastIndex - 1;
            return lastIndex - 1;
          }
        }
      }

      // Fall back to binary search for larger jumps
      let left = 0;
      let right = items.length - 1;
      let lastValidItemIndex = -1;

      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const item = items[mid];

        if (currentTime >= item.startTime && currentTime <= item.endTime) {
          lastItemIndexRef.current = mid;
          return mid;
        } else if (currentTime < item.startTime) {
          right = mid - 1;
        } else {
          lastValidItemIndex = mid;
          left = mid + 1;
        }
      }

      lastItemIndexRef.current = lastValidItemIndex;
      return lastValidItemIndex;
    },
    [items, rangeStartTime, rangeEndTime]
  );

  return getCurrentItemIndex;
};
